import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import {
  Avatar,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Typography,
} from '@/ui';

import {
  clearSingleBookingAttempt,
  confirmedSingleAppointment,
  loadSingleBookingAttempt,
  saveSingleBookingAttempt,
} from './bookingAttemptState';

import type { StoredSingleBookingAttempt } from './bookingAttemptState';
import type {
  BrowserSchedulingContextV3,
  DecodedAppointmentMutationResult,
  DecodedBrowserAppointment,
  ParticipantAppointmentDetail,
  ParticipantAppointmentMutationResult,
  ParticipantSchedulingContext,
} from '@/features/appointments/types';
import type { BookAppointmentRequestV3 } from '@/store/api/appointmentApi';
import type { BookMomentsParticipantAppointmentRequest } from '@/store/api/momentsParticipantAppointmentApi';

import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import { MENTO_LOGO_URL } from '@/components/MentoCal/constants';
import MemberBoundBooking from '@/features/appointments/MemberBoundBooking';
import PlatformAdminBookingPage from '@/features/appointments/PlatformAdminBookingPage';
import { appointmentRejectionMessage } from '@/features/appointments/appointmentStatusCopy';
import { SINGLE_SESSION_APPOINTMENT_CONTRACT } from '@/features/appointments/browserCapability';
import { useOnBehalfBookingTarget } from '@/features/appointments/onBehalfBookingTarget';
import {
  appointmentApi,
  trustedAppointmentDeclineMessage,
  useBookAppointmentMutation,
  useGetAppointmentOperationQuery,
  useGetAppointmentSchedulingContextQuery,
  useLazyGetAppointmentQuery,
} from '@/store/api/appointmentApi';
import {
  useBookMomentsParticipantAppointmentMutation,
  useGetMomentsParticipantAppointmentOperationQuery,
  useGetMomentsParticipantSchedulingContextQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';
import { useAppDispatch } from '@/store/hooks';

const DIVIDER = '#E3E3E3';
const DEFAULT_VISIBLE_WEEKS = 2;
const RECONCILIATION_MESSAGE =
  'We could not confirm this booking with Google. Mento support must review this booking before you try another time.';
const SUBMISSION_UNKNOWN_MESSAGE =
  'We could not safely determine whether this booking was submitted. Contact Mento support before trying another time.';

const needsReconciliationAttempt = (
  attempt: StoredSingleBookingAttempt,
  operationId: string,
): StoredSingleBookingAttempt => ({
  ...attempt,
  operationId,
  status: 'needs_reconciliation',
});

const viewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const requestErrorMessage = (error: unknown) => {
  // A declared non-write already arrives member-safe: the API layer translates
  // the upstream reason token before any surface sees it. Asking for the
  // booking-specific sentence only upgrades "this request was not completed"
  // to "this booking was not made"; the passthrough below can no longer carry
  // a token either way.
  const declined = trustedAppointmentDeclineMessage('booking', error);
  if (declined) return declined;

  if (error && typeof error === 'object' && 'data' in error) {
    const { data } = error as { data?: { error?: unknown } };
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  }
  return 'We could not confirm this booking. Retry the same request before choosing another time.';
};

const ParticipantSingleBooking = () => {
  const { coachId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isUserLoading, user } = useCurrentUser();
  const timezone = useMemo(viewerTimezone, []);
  const ownerKey = user.id ? `${user.id}:${coachId}:one_off` : '';
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStart, setSelectedStart] = useState('');
  const [attempt, setAttempt] = useState<StoredSingleBookingAttempt | null>(
    null,
  );
  const [hydratedOwnerKey, setHydratedOwnerKey] = useState('');
  const [error, setError] = useState('');
  const submissionClaimed = useRef(false);
  const routeOwner = useRef({ generation: 0, ownerKey });
  if (routeOwner.current.ownerKey !== ownerKey) {
    routeOwner.current = {
      generation: routeOwner.current.generation + 1,
      ownerKey,
    };
  }

  const ownedAttempt = attempt?.ownerKey === ownerKey ? attempt : null;
  const attemptHydrated = hydratedOwnerKey === ownerKey;
  const routeDecision = useParticipantAppointmentRouteDecision({
    coachExternalId: attemptHydrated ? coachId : undefined,
    establishedTransport: ownedAttempt
      ? (ownedAttempt.transport ?? 'rails')
      : undefined,
    flow: 'booking',
  });
  const selectedTransport =
    routeDecision.state === 'ready' ? routeDecision.transport : undefined;
  const contextQuery = useGetAppointmentSchedulingContextQuery(
    coachId && selectedTransport === 'rails'
      ? {
          bookingPattern: 'one_off',
          browserContractMarker: SINGLE_SESSION_APPOINTMENT_CONTRACT,
          coachId,
          kind: 'coaching',
          viewerTimezone: timezone,
          visibleWeeks: DEFAULT_VISIBLE_WEEKS,
        }
      : skipToken,
  );
  const momentsContextQuery = useGetMomentsParticipantSchedulingContextQuery(
    coachId && selectedTransport === 'moments'
      ? {
          bookingPattern: 'one_off',
          counterpart: { externalId: coachId, provider: 'legacy_app' },
          kind: 'coaching',
          viewerTimezone: timezone,
          visibleWeeks: DEFAULT_VISIBLE_WEEKS,
        }
      : skipToken,
  );
  const railsContext =
    contextQuery.data?.contractVersion === 'appointment-scheduling-context.v3'
      ? (contextQuery.data as BrowserSchedulingContextV3)
      : undefined;
  const momentsContext =
    momentsContextQuery.data?.contractVersion ===
    'appointment-participant-scheduling-context.v1'
      ? (momentsContextQuery.data as ParticipantSchedulingContext)
      : undefined;
  const context =
    selectedTransport === 'moments' ? momentsContext : railsContext;
  const [bookAppointment, bookingResult] = useBookAppointmentMutation();
  const [bookMomentsAppointment, momentsBookingResult] =
    useBookMomentsParticipantAppointmentMutation();
  const bookingIsLoading =
    bookingResult.isLoading || momentsBookingResult.isLoading;
  const [getAppointment] = useLazyGetAppointmentQuery();
  const [getMomentsAppointment] =
    useLazyGetMomentsParticipantAppointmentQuery();
  const operationQuery = useGetAppointmentOperationQuery(
    ownedAttempt?.status === 'pending' &&
      ownedAttempt.operationId &&
      (ownedAttempt.transport ?? 'rails') === 'rails'
      ? {
          browserContractMarker: 'browser-appointments.booking-pattern-v3',
          operationId: ownedAttempt.operationId,
        }
      : skipToken,
    { pollingInterval: 2000 },
  );
  const momentsOperationQuery =
    useGetMomentsParticipantAppointmentOperationQuery(
      ownedAttempt?.status === 'pending' &&
        ownedAttempt.operationId &&
        ownedAttempt.transport === 'moments'
        ? { operationId: ownedAttempt.operationId }
        : skipToken,
      { pollingInterval: 2000 },
    );

  useEffect(() => {
    if (!ownerKey) return;
    const restored = loadSingleBookingAttempt(ownerKey);
    submissionClaimed.current =
      restored?.status === 'submitting' ||
      restored?.status === 'pending' ||
      restored?.status === 'needs_reconciliation' ||
      restored?.status === 'submission_unknown' ||
      restored?.status === 'confirmed';
    setAttempt(restored);
    setHydratedOwnerKey(ownerKey);
  }, [ownerKey]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate('/m');
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [navigate]);

  const acceptResult = (
    result: Parameters<typeof confirmedSingleAppointment>[0]['result'],
    currentAttempt: StoredSingleBookingAttempt,
  ) => {
    const appointment = confirmedSingleAppointment({
      coachPersonId: currentAttempt.identity.coachPersonId,
      memberPersonId: currentAttempt.identity.memberPersonId,
      result,
      seatType: currentAttempt.identity.seatType,
      startAt: currentAttempt.command.startAt,
    });
    if (!appointment) return false;
    const confirmedAttempt: StoredSingleBookingAttempt = {
      ...currentAttempt,
      confirmation: {
        endAt: appointment.currentSchedule.endAt,
        startAt: appointment.currentSchedule.startAt,
      },
      confirmationInvalidated: false,
      operationId: result.operationId,
      status: 'confirmed',
    };
    if (!saveSingleBookingAttempt(confirmedAttempt)) return false;
    setAttempt(confirmedAttempt);
    return true;
  };

  useEffect(() => {
    if (
      attempt?.status !== 'confirmed' ||
      !attempt.confirmation ||
      attempt.confirmationInvalidated
    )
      return;
    const invalidatedAttempt = {
      ...attempt,
      confirmationInvalidated: true,
    };
    if (!saveSingleBookingAttempt(invalidatedAttempt)) return;
    setAttempt(invalidatedAttempt);
    dispatch(
      appointmentApi.util.invalidateTags([
        { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
        { id: 'LIST', type: 'Appointment' },
        'Session',
        'User',
      ]),
    );
  }, [attempt, dispatch]);

  useEffect(() => {
    const transport = ownedAttempt?.transport ?? 'rails';
    const operation =
      transport === 'moments'
        ? momentsOperationQuery.currentData
        : operationQuery.currentData;
    if (
      !ownedAttempt ||
      ownedAttempt.status !== 'pending' ||
      !operation ||
      operation.id !== ownedAttempt.operationId
    )
      return;
    if (operation.status === 'pending') return;
    if (operation.status === 'confirmed') {
      if (operation.appointmentIds.length !== 1) {
        const invalidAttempt = { ...ownedAttempt, status: 'error' as const };
        saveSingleBookingAttempt(invalidAttempt);
        setAttempt(invalidAttempt);
        setError(
          'The booking result did not match this single-session request.',
        );
        return;
      }
      const routeOwnerGeneration = routeOwner.current.generation;
      const ownsCurrentRoute = () =>
        routeOwner.current.generation === routeOwnerGeneration &&
        routeOwner.current.ownerKey === ownedAttempt.ownerKey;
      const acceptDetail = (
        appointment: DecodedBrowserAppointment | ParticipantAppointmentDetail,
      ) => {
        if (!ownsCurrentRoute()) return;
        const result =
          transport === 'moments'
            ? {
                appointments: [appointment],
                contractVersion:
                  'appointment-participant-mutation-result.v1' as const,
                error: momentsOperationQuery.currentData!.error,
                operationId: operation.id,
                reconciliationReason:
                  momentsOperationQuery.currentData!.reconciliationReason,
                retryAfterSeconds: operation.retryAfterSeconds,
                status: operation.status,
                writeOutcome: momentsOperationQuery.currentData!.writeOutcome,
              }
            : {
                appointments: [appointment],
                contractVersion: 'appointment-mutation.v1' as const,
                operationId: operation.id,
                rejection: operationQuery.currentData!.rejection,
                retryAfterSeconds: operation.retryAfterSeconds,
                status: operation.status,
              };
        if (acceptResult(result, ownedAttempt)) return;
        throw new Error('mismatch');
      };
      const detailPromise =
        transport === 'moments'
          ? getMomentsAppointment({
              appointmentId: operation.appointmentIds[0]!,
            }).unwrap()
          : getAppointment({
              appointmentId: operation.appointmentIds[0]!,
              userId: user.id,
            }).unwrap();
      detailPromise.then(acceptDetail).catch(() => {
        if (!ownsCurrentRoute()) return;
        const invalidAttempt = { ...ownedAttempt, status: 'error' as const };
        saveSingleBookingAttempt(invalidAttempt);
        setAttempt(invalidAttempt);
        setError(
          'The booking result did not match this single-session request.',
        );
      });
      return;
    }
    if (operation.status === 'needs_reconciliation') {
      const retainedAttempt = needsReconciliationAttempt(
        ownedAttempt,
        operation.id,
      );
      saveSingleBookingAttempt(retainedAttempt);
      setAttempt(retainedAttempt);
      setError(RECONCILIATION_MESSAGE);
      return;
    }
    clearSingleBookingAttempt(ownerKey);
    setAttempt(null);
    setError(
      transport === 'moments'
        ? requestErrorMessage({
            data: momentsOperationQuery.currentData!.error,
          })
        : appointmentRejectionMessage(
            operationQuery.currentData!.rejection,
            'booking',
          ),
    );
    submissionClaimed.current = false;
  }, [
    ownedAttempt,
    context,
    getAppointment,
    getMomentsAppointment,
    momentsOperationQuery.currentData,
    operationQuery.currentData,
    ownerKey,
    user.id,
  ]);

  const selectedDay = context?.availability.days.find(
    ({ date }) => date === selectedDate,
  );
  const selectedSlot = selectedDay?.slots.find(
    ({ startAt }) => startAt === selectedStart,
  );
  const calendarDays =
    context?.availability.days.map(({ date, enabled }) => ({
      date,
      enabled,
    })) ?? [];

  const submit = async (
    commandOverride?:
      BookAppointmentRequestV3 | BookMomentsParticipantAppointmentRequest,
  ) => {
    if (!context?.seatType || !selectedTransport || submissionClaimed.current)
      return;
    let command = commandOverride;
    if (!command && selectedSlot) {
      command =
        selectedTransport === 'moments'
          ? {
              bookingPattern: 'one_off' as const,
              counterpart: { externalId: coachId, provider: 'legacy_app' },
              idempotencyKey: `member-single-booking:${user.id}:${crypto.randomUUID()}`,
              kind: 'coaching' as const,
              startAt: selectedSlot.startAt,
              viewerTimezone: timezone,
            }
          : {
              bookingPattern: { type: 'one_off' as const },
              browserContractMarker: SINGLE_SESSION_APPOINTMENT_CONTRACT,
              coachId,
              contractVersion: 'appointment-book.v3' as const,
              expectedCalendarEventTitle: railsContext!.calendarEventTitle,
              idempotencyKey: `member-single-booking:${user.id}:${crypto.randomUUID()}`,
              kind: 'coaching' as const,
              startAt: selectedSlot.startAt,
              viewerTimezone: timezone,
            };
    }
    if (!command) return;
    submissionClaimed.current = true;
    const submitting: StoredSingleBookingAttempt = {
      command: command as StoredSingleBookingAttempt['command'],
      confirmation: null,
      confirmationInvalidated: false,
      identity: {
        coachPersonId: context.coach.personId,
        memberPersonId: context.member.personId,
        seatType: context.seatType,
      },
      operationId: null,
      ownerKey,
      presentation: {
        coachAvatarUrl: context.coach.avatarUrl,
        coachDisplayName: context.coach.displayName,
        durationMinutes: context.durationMinutes,
        memberEmail: context.member.email,
        timezone: context.timezone,
      },
      status: 'submitting',
      transport: selectedTransport,
      version: 1,
    };
    if (!saveSingleBookingAttempt(submitting)) {
      submissionClaimed.current = false;
      setError(
        'Browser storage is unavailable, so this booking cannot be submitted safely.',
      );
      return;
    }
    setAttempt(submitting);
    setError('');
    try {
      const result =
        selectedTransport === 'moments'
          ? await bookMomentsAppointment(
              command as BookMomentsParticipantAppointmentRequest,
            ).unwrap()
          : await bookAppointment(command as BookAppointmentRequestV3).unwrap();
      if (result.status === 'confirmed' && acceptResult(result, submitting))
        return;
      if (result.status === 'pending') {
        const pending: StoredSingleBookingAttempt = {
          ...submitting,
          operationId: result.operationId,
          status: 'pending',
        };
        saveSingleBookingAttempt(pending);
        setAttempt(pending);
        return;
      }
      if (result.status === 'needs_reconciliation') {
        const retainedAttempt = needsReconciliationAttempt(
          submitting,
          result.operationId,
        );
        saveSingleBookingAttempt(retainedAttempt);
        setAttempt(retainedAttempt);
        setError(RECONCILIATION_MESSAGE);
        return;
      }
      if (result.status === 'rejected') {
        clearSingleBookingAttempt(ownerKey);
        setAttempt(null);
        setError(
          selectedTransport === 'moments'
            ? requestErrorMessage({
                data: (result as ParticipantAppointmentMutationResult).error,
              })
            : appointmentRejectionMessage(
                (result as DecodedAppointmentMutationResult).rejection,
                'booking',
              ),
        );
        submissionClaimed.current = false;
        return;
      }
      throw new Error('Single-session response did not match the request');
    } catch (bookingError) {
      const retry: StoredSingleBookingAttempt = {
        ...submitting,
        status: 'error',
      };
      saveSingleBookingAttempt(retry);
      setAttempt(retry);
      setError(requestErrorMessage(bookingError));
      submissionClaimed.current = false;
    }
  };

  const retry = () => {
    if (
      !ownedAttempt ||
      (ownedAttempt.status !== 'retry' && ownedAttempt.status !== 'error')
    )
      return;
    submissionClaimed.current = false;
    submit(ownedAttempt.command).catch(() => undefined);
  };

  if (ownedAttempt?.status === 'confirmed' && ownedAttempt.confirmation) {
    const start = new Date(ownedAttempt.confirmation.startAt);
    const end = new Date(ownedAttempt.confirmation.endAt);
    const dateTimeOptions = {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: ownedAttempt.presentation.timezone,
    } as const;
    return (
      <main className="relative flex min-h-screen items-center justify-center bg-sunny-100 px-4 py-8">
        <IconButton
          aria-label="Close"
          className="absolute right-4 top-4"
          onClick={() => navigate('/m')}
        >
          <Icon>close</Icon>
        </IconButton>
        <Paper
          aria-label="Single session confirmed"
          className="w-full max-w-[481px] p-8"
          role="region"
        >
          <Typography className="text-2xl font-bold" variant="h5">
            Your coaching session is set!
          </Typography>
          <Typography className="mt-3 text-cement-400">
            {start.toLocaleString('en', dateTimeOptions)} to{' '}
            {end.toLocaleString('en', dateTimeOptions)}{' '}
            {ownedAttempt.presentation.timezone}
          </Typography>
          <Typography className="mt-2 text-cement-400">
            {ownedAttempt.presentation.memberEmail}
          </Typography>
          <Typography className="mt-2 text-cement-400">Google Meet</Typography>
          <div className="mt-6 flex justify-end">
            <Button
              className="rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-600"
              variant="contained"
              onClick={() => {
                clearSingleBookingAttempt(ownerKey);
                navigate('/m/sessions/upcoming');
              }}
            >
              Done
            </Button>
          </div>
        </Paper>
      </main>
    );
  }

  if (
    ownedAttempt?.status === 'pending' ||
    ownedAttempt?.status === 'submitting'
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="max-w-md p-8 text-center" role="status">
          <Typography variant="h5">Confirming your booking</Typography>
          <Typography className="mt-2 text-cement-400">
            Keep this page open. We are confirming this single session with
            Google.
          </Typography>
        </Paper>
      </main>
    );
  }

  if (ownedAttempt?.status === 'needs_reconciliation') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="max-w-md p-8 text-center" role="alert">
          <Typography variant="h5">This booking needs review</Typography>
          <Typography className="mt-2 text-cement-400">
            {RECONCILIATION_MESSAGE}
          </Typography>
        </Paper>
      </main>
    );
  }

  if (ownedAttempt?.status === 'submission_unknown') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="max-w-md p-8 text-center" role="alert">
          <Typography variant="h5">This booking needs review</Typography>
          <Typography className="mt-2 text-cement-400">
            {SUBMISSION_UNKNOWN_MESSAGE}
          </Typography>
        </Paper>
      </main>
    );
  }

  if (
    isUserLoading ||
    !attemptHydrated ||
    routeDecision.state === 'loading' ||
    contextQuery.isLoading ||
    momentsContextQuery.isLoading
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100">
        <CircularProgress aria-label="Loading booking details" size={24} />
      </main>
    );
  }

  if (routeDecision.state === 'unavailable' || !context) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="max-w-md p-8 text-center" role="alert">
          <Typography variant="h5">
            We could not load this booking link.
          </Typography>
          <Typography className="mt-2 text-cement-400">
            Sign in with the member account assigned to this coach and try
            again.
          </Typography>
        </Paper>
      </main>
    );
  }

  if (!context.eligibility.eligible || context.seatType === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="max-w-md p-8 text-center" role="alert">
          <Typography variant="h5">This session cannot be booked.</Typography>
          <Typography className="mt-2 text-cement-400">
            Please contact Mento support for help with this booking link.
          </Typography>
          <Button className="mt-6" onClick={() => navigate('/m')}>
            Back to home
          </Button>
        </Paper>
      </main>
    );
  }

  const [coachFirstName = '', ...coachLastName] =
    context.coach.displayName.split(' ');
  const seatLabel =
    context.seatType === 'executive' ? 'Executive' : 'Leadership';

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center gap-10 bg-sunny-100 px-4 py-8">
      <IconButton
        aria-label="Close"
        className="absolute right-4 top-4"
        onClick={() => navigate('/m')}
      >
        <Icon>close</Icon>
      </IconButton>
      <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />
      <Paper
        aria-label="Book a single coaching session"
        className="flex w-full max-w-[1141px] flex-col overflow-hidden lg:h-[421px] lg:flex-row"
        elevation={3}
        role="region"
        style={{ border: `1px solid ${DIVIDER}`, borderRadius: 16 }}
      >
        <section
          className="flex shrink-0 flex-col gap-4 border-b p-6 sm:p-8 lg:h-full lg:min-h-0 lg:w-[379px] lg:overflow-y-auto lg:border-b-0 lg:border-r"
          style={{ borderColor: DIVIDER }}
        >
          <div className="flex items-center gap-3.5">
            <Avatar
              firstName={coachFirstName}
              lastName={coachLastName.join(' ')}
              size="s"
              src={context.coach.avatarUrl ?? ''}
            />
            <Typography className="font-bold">
              {context.coach.displayName}
            </Typography>
          </div>
          <Typography className="text-2xl font-bold" variant="h5">
            Book a single coaching session
          </Typography>
          <Typography className="text-cement-400">
            Choose one available time with {coachFirstName}.
          </Typography>
          <div className="flex flex-col gap-3 text-sm text-cement-400">
            <span>
              <Icon className="mr-1 text-base">person</Icon>
              {context.member.email}
            </span>
            <span>
              <Icon className="mr-1 text-base">event_seat</Icon>Seat type:{' '}
              {seatLabel}
            </span>
            <span>
              <Icon className="mr-1 text-base">schedule</Icon>
              {context.durationMinutes} Minutes
            </span>
            <span>
              <Icon className="mr-1 text-base">videocam</Icon>Google Meet
            </span>
            <span>
              <Icon className="mr-1 text-base">language</Icon>
              {context.timezone}
            </span>
          </div>
        </section>
        <section
          aria-label="Choose a date"
          className="border-b p-6 sm:p-8 lg:w-[407px] lg:border-b-0 lg:border-r"
          style={{ borderColor: DIVIDER }}
        >
          <MentoCalDatePicker
            canShowMore={false}
            days={calendarDays}
            isLoadingMore={false}
            selectedDate={selectedDate}
            onSelectDate={(date) => {
              setSelectedDate(date);
              setSelectedStart('');
            }}
            onShowMore={() => undefined}
          />
        </section>
        <section
          aria-label="Choose a time"
          className="flex min-h-[260px] flex-1 flex-col gap-4 p-6 sm:p-8 lg:h-full lg:min-h-0 lg:w-[355px]"
        >
          <div
            aria-label="Available times"
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
          >
            {selectedDay?.slots.map((slot) => (
              <button
                key={slot.startAt}
                aria-pressed={slot.startAt === selectedStart}
                className="min-h-10 shrink-0 rounded-lg border border-solid border-cement-200 bg-white px-3 py-2 text-sm font-medium text-cement-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500 aria-pressed:border-2 aria-pressed:border-sage-400 aria-pressed:bg-sage-200"
                type="button"
                onClick={() => setSelectedStart(slot.startAt)}
              >
                {slot.label}
              </button>
            ))}
            {selectedDay && selectedDay.slots.length === 0 ? (
              <Typography>No times available.</Typography>
            ) : null}
          </div>
          <div className="shrink-0">
            {error ? (
              <Typography className="mb-2 text-sm text-red-600" role="alert">
                {error}
              </Typography>
            ) : null}
            {ownedAttempt?.status === 'retry' ||
            ownedAttempt?.status === 'error' ? (
              <Button
                className="w-full"
                disabled={bookingIsLoading}
                onClick={retry}
              >
                Retry booking
              </Button>
            ) : (
              <Button
                className="w-full rounded-xl bg-sage-500 px-5 py-2 text-[15px] font-bold tracking-[0.0267em] text-white shadow-none hover:bg-sage-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-600 disabled:bg-sage-100 disabled:text-cement-300"
                disabled={!selectedSlot || bookingIsLoading}
                variant="contained"
                onClick={() => submit()}
              >
                {bookingIsLoading ? 'Booking...' : 'Book'}
              </Button>
            )}
          </div>
        </section>
      </Paper>
    </main>
  );
};

const MemberSingleBooking = () => {
  const onBehalf = useOnBehalfBookingTarget();
  const navigate = useNavigate();
  if (onBehalf) {
    return (
      <PlatformAdminBookingPage
        bookingType="single"
        coachId={onBehalf.coachId}
        kind="coaching"
        memberId={onBehalf.memberId}
        onDone={() => navigate(onBehalf.returnPath)}
      />
    );
  }
  return (
    <MemberBoundBooking>
      <ParticipantSingleBooking />
    </MemberBoundBooking>
  );
};

export default MemberSingleBooking;
